<template>
  <v-card outlined class="pa-4">
    <h2 class="mdn-Heading mdn-Heading--xs mb-4">Dashboard Telmex x Claro Geral</h2>

    <form class="dashboard-filtros" @submit.prevent="filtrar">
      <div class="mdn-Row">
        <div class="mdn-Col-12 mdn-Col-md-3 mdn-u-marginTop--xs">
          <SGPFormSelectComponent
            name="ctrl_provedor_id"
            v-model="filtros.ctrl_provedor_id"
            :listaDeOptions="opcoesProvedores"
            optionValue="value"
            optionLabel="text"
            placeholder="Provedor"
          />
        </div>

        <div class="mdn-Col-12 mdn-Col-md-3 mdn-u-marginTop--xs">
          <SGPFormSelectComponent
            name="centro_funcional"
            v-model="filtros.centro_funcional"
            :listaDeOptions="opcoesCentroFuncional"
            optionValue="value"
            optionLabel="text"
            placeholder="Centro Funcional"
          />
        </div>

        <div class="mdn-Col-12 mdn-Col-md-2 mdn-u-marginTop--xs">
          <SGPFormInputComponent
            type="date"
            name="data_inicio"
            v-model="filtros.data_inicio"
            placeholder="Data início"
          />
        </div>

        <div class="mdn-Col-12 mdn-Col-md-2 mdn-u-marginTop--xs">
          <SGPFormInputComponent
            type="date"
            name="data_fim"
            v-model="filtros.data_fim"
            placeholder="Data fim"
          />
        </div>

        <div class="mdn-Col-12 mdn-Col-md-2 mdn-u-marginTop--xs dashboard-acoes">
          <button
            type="submit"
            class="v-btn v-btn--has-bg theme--light v-size--default primary text-capitalize"
          >
            Filtrar
          </button>

          <button
            type="button"
            class="v-btn v-btn--outlined theme--light v-size--default grey-5--text text-capitalize"
            @click="limpar"
          >
            Limpar
          </button>
        </div>
      </div>
    </form>

    <SGPAlertaComponent v-if="erro">{{ erro }}</SGPAlertaComponent>

    <SGPAlertaComponent v-else-if="!loadingDashboard && semDados">
      Nenhum ticket no período
    </SGPAlertaComponent>

    <template v-else-if="!semDados">
      <div class="dashboard-cards">
        <v-card
          v-for="card in cards"
          :key="card.label"
          outlined
          class="dashboard-card"
          :style="{ borderLeftColor: card.cor }"
        >
          <span class="dashboard-card-label">{{ card.label }}</span>
          <strong class="dashboard-card-valor">{{ card.valor }}</strong>
        </v-card>
      </div>

      <div class="dashboard-graficos">
        <v-card outlined class="pa-3 dashboard-grafico-barras">
          <h3 class="mdn-Heading mdn-Heading--xxs mb-2">Tickets por provedor</h3>
          <SGPGraficoComponent
            tipoGrafico="bar"
            :opcoesGrafico="opcoesBarras"
            :series="seriesBarras"
            height="340"
          />
        </v-card>

        <v-card outlined class="pa-3 dashboard-grafico-rosca">
          <h3 class="mdn-Heading mdn-Heading--xxs mb-2">Telmex x Claro Geral</h3>
          <SGPGraficoComponent
            tipoGrafico="donut"
            :opcoesGrafico="opcoesRosca"
            :series="seriesRosca"
            height="340"
          />
        </v-card>
      </div>

      <SGPPaginacaoComponent
        class="mt-6"
        :paginacao="paginacaoDashboard"
        :key="`${paginacaoDashboard.current_page}-${paginacaoDashboard.per_page}`"
        @sgpAtualizarPaginacao="realizarPaginacao"
      >
      <v-simple-table class="mt-4" dense>
        <thead>
          <tr>
            <th>Provedor</th>
            <th class="text-right">Telmex</th>
            <th class="text-right">Claro Geral</th>
            <th class="text-right">Total</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="linha in linhasTabela" :key="linha.chave">
            <td>{{ linha.provedor_nome }}</td>
            <td class="text-right">{{ linha.telmex }}</td>
            <td class="text-right">{{ linha.claro_geral }}</td>
            <td class="text-right">{{ linha.total }}</td>
          </tr>
        </tbody>
        <tfoot>
          <tr class="dashboard-total">
            <td>Total geral</td>
            <td class="text-right">{{ totais.telmex }}</td>
            <td class="text-right">{{ totais.claro_geral }}</td>
            <td class="text-right">{{ totais.total }}</td>
          </tr>
        </tfoot>
      </v-simple-table>
      </SGPPaginacaoComponent>
    </template>

    <SGPLoadingComponent v-if="loadingDashboard" />
  </v-card>
</template>

<script>
import moment from 'moment'
import { mapActions, mapGetters } from 'vuex'
import {
  centroFuncionalCores,
  centroFuncionalLabels,
  centroFuncionalOpcoes,
} from '@/common/helpers/centroFuncional'

const criarFiltrosPadrao = () => ({
  ctrl_provedor_id: '',
  centro_funcional: '',
  data_inicio: '',
  data_fim: '',
})

const coresSeries = [
  centroFuncionalCores.TELMEX,
  centroFuncionalCores.CLARO_GERAL,
  centroFuncionalCores.semCf,
]

// A série Total tem valor 0 para não somar na pilha; o valor real aparece só no tooltip.
const INDICE_SERIE_TOTAL = 2

export default {
  name: 'IntegracaoDashboardComponent',

  data() {
    return {
      filtros: criarFiltrosPadrao(),
      opcoesCentroFuncional: centroFuncionalOpcoes,
      erro: null,
    }
  },

  async created() {
    await Promise.all([
      this.listarProvedores(),
      this.carregar(),
    ])
  },

  computed: {
    ...mapGetters('pcoWorkflowTemp', ['dashboardCentroFuncional', 'loadingDashboard', 'paginacaoDashboard']),
    ...mapGetters('ctrlProvedores', ['listaDeProvedores']),

    opcoesProvedores() {
      return [
        { value: '', text: 'Todos' },
        ...(this.listaDeProvedores || []).map(item => ({ value: item.id, text: item.nome })),
      ]
    },

    provedores() {
      return this.dashboardCentroFuncional?.provedores || []
    },

    totais() {
      return this.dashboardCentroFuncional?.totais || { telmex: 0, claro_geral: 0, total: 0 }
    },

    semDados() {
      return !this.provedores.length || !Number(this.totais.total)
    },

    cards() {
      return [
        { label: centroFuncionalLabels.TELMEX, valor: this.totais.telmex, cor: centroFuncionalCores.TELMEX },
        { label: centroFuncionalLabels.CLARO_GERAL, valor: this.totais.claro_geral, cor: centroFuncionalCores.CLARO_GERAL },
        { label: 'Total', valor: this.totais.total, cor: centroFuncionalCores.semCf },
      ]
    },

    linhasTabela() {
      return this.provedores.map((p, index) => ({
        chave: p.ctrl_provedor_id ?? `sem-provedor-${index}`,
        provedor_nome: p.provedor_nome || 'Sem provedor',
        telmex: p.telmex,
        claro_geral: p.claro_geral,
        total: p.total,
      }))
    },

    seriesBarras() {
      return [
        { name: centroFuncionalLabels.TELMEX, data: this.linhasTabela.map(l => l.telmex) },
        { name: centroFuncionalLabels.CLARO_GERAL, data: this.linhasTabela.map(l => l.claro_geral) },
        { name: 'Total', data: this.linhasTabela.map(() => 0) },
      ]
    },

    opcoesBarras() {
      return {
        chart: { type: 'bar', stacked: true, toolbar: { show: false } },
        colors: coresSeries,
        plotOptions: { bar: { horizontal: false, columnWidth: '50%' } },
        dataLabels: { enabled: false },
        xaxis: { categories: this.linhasTabela.map(l => l.provedor_nome) },
        legend: { position: 'bottom' },
        tooltip: {
          shared: true,
          intersect: false,
          y: {
            formatter: (valor, { seriesIndex, dataPointIndex } = {}) => {
              if (seriesIndex === INDICE_SERIE_TOTAL) {
                return this.linhasTabela[dataPointIndex]?.total ?? 0
              }
              return valor
            },
          },
        },
      }
    },

    seriesRosca() {
      return [Number(this.totais.telmex) || 0, Number(this.totais.claro_geral) || 0]
    },

    opcoesRosca() {
      return {
        chart: { type: 'donut' },
        labels: [centroFuncionalLabels.TELMEX, centroFuncionalLabels.CLARO_GERAL],
        colors: [centroFuncionalCores.TELMEX, centroFuncionalCores.CLARO_GERAL],
        legend: { position: 'bottom' },
      }
    },
  },

  methods: {
    ...mapActions('pcoWorkflowTemp', ['buscarDashboardCentroFuncional']),
    ...mapActions('ctrlProvedores', ['listarProvedores']),

    montarParametros(paginacao = {}) {
      const params = Object.entries(this.filtros).reduce((acc, [chave, valor]) => {
        if (valor !== '' && valor !== null && valor !== undefined) {
          acc[chave] = valor
        }
        return acc
      }, {})

      return {
        ...params,
        current_page: paginacao.current_page || 1,
        per_page: paginacao.per_page || this.paginacaoDashboard?.per_page || 20,
      }
    },

    periodoInvalido() {
      const { data_inicio, data_fim } = this.filtros
      return Boolean(data_inicio && data_fim && moment(data_fim).isBefore(moment(data_inicio)))
    },

    async carregar(paginacao = {}) {
      if (this.periodoInvalido()) {
        this.erro = 'A data fim deve ser maior ou igual à data início.'
        return
      }

      this.erro = null

      try {
        await this.buscarDashboardCentroFuncional(this.montarParametros(paginacao))
      } catch (error) {
        this.erro = error?.response?.data?.message?.errors?.[0]
          || 'Não foi possível carregar o dashboard.'
      }
    },

    filtrar() {
      this.carregar()
    },

    limpar() {
      this.filtros = criarFiltrosPadrao()
      this.carregar()
    },

    realizarPaginacao(paginate) {
      this.carregar({
        current_page: paginate.current_page || 1,
        per_page: paginate.per_page || this.paginacaoDashboard.per_page,
      })
    },
  },
}
</script>

<style scoped>
.dashboard-filtros {
  margin-bottom: 16px;
}

.dashboard-filtros .mdn-Row {
  display: grid;
  grid-template-columns: minmax(200px, 2fr) minmax(170px, 1.5fr) minmax(150px, 1fr) minmax(150px, 1fr) auto;
  gap: 12px;
  align-items: center;
  margin: 0;
}

.dashboard-filtros .mdn-Row > div {
  width: auto;
  max-width: none;
  margin: 0;
  padding: 0;
}

.dashboard-filtros :deep(.v-text-field__details) {
  display: none !important;
}

.dashboard-filtros :deep(.v-input) {
  margin: 0;
  padding: 0;
}

.dashboard-acoes {
  display: flex;
  gap: 12px;
  align-items: center;
  justify-content: flex-end;
}

@media (max-width: 1100px) {
  .dashboard-filtros .mdn-Row {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .dashboard-acoes {
    grid-column: 1 / -1;
  }
}

.dashboard-cards {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  margin-bottom: 16px;
}

.dashboard-card {
  flex: 1 1 180px;
  display: flex;
  flex-direction: column;
  padding: 12px 16px;
  border-left-width: 4px !important;
  border-left-style: solid !important;
}

.dashboard-card-label {
  color: #757575;
  font-size: 14px;
}

.dashboard-card-valor {
  font-size: 24px;
}

.dashboard-graficos {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
}

.dashboard-grafico-barras {
  flex: 2 1 420px;
}

.dashboard-grafico-rosca {
  flex: 1 1 300px;
}

.dashboard-tabela {
  margin-top: 50px !important;
}

.dashboard-total td {
  font-weight: 600;
}

@media (max-width: 768px) {
  .dashboard-filtros .mdn-Row {
    grid-template-columns: 1fr;
  }

  .dashboard-acoes {
    flex-direction: column;
    align-items: stretch;
  }
}
</style>